// @flow
import { createAction } from 'redux-actions';
import { v1 as uuidv1 } from 'uuid';

import type { ActionType, Thunk } from 'types/redux';
import * as actionTypes from './actionTypes';
import { APP_FLOW } from 'constants/appFlow';
import { APP_NAMES } from 'constants/appName';

// Selectors
import {
  getConsumerAllowAnyGreeterSel,
  getTellUsMoreSvcVisible,
  getOther,
  getQuickAppointment
} from 'store/reducers/dealerSettings/selectors';

export const setPrivateRoute = createAction(
  actionTypes.SET_PRIVATE_ROUTE,
  (enable: boolean) => enable
);

export const setApplicationFilterDaysBy = createAction(
  actionTypes.SET_APPOINTMENT_FILTER_DAYS_BY,
  (filter: 'morning' | 'afternoon') => filter
);

export const setAppointmentSelectedDay = createAction(
  actionTypes.SET_APPOINTMENT_SELECTED_DAY,
  (selectedDay: any) => selectedDay
);

export const setAppFlow = createAction(
  actionTypes.SET_APP_FLOW,
  (appFlow: $Values<typeof APP_FLOW>) => appFlow
);

export const setAppName = createAction(
  actionTypes.SET_APP_NAME,
  (appName: $Values<typeof APP_NAMES>) => appName
);

export const setGtmFirstAvailableTimeslot = createAction(
  actionTypes.SET_GTM_FIRST_AVAILABLE_TIMESLOT,
  (first: Date) => first
);

export const setNoFindsAlertShown = createAction(
  actionTypes.SET_NO_FINDS_ALERT_SHOWN,
  (shown: boolean) => shown
);

export const setShowMobileFooter = createAction(
  actionTypes.SET_SHOW_MOBILE_FOOTER,
  (show: boolean) => show
);

export const setVehiclePageRevisit = createAction(
  actionTypes.SET_VEHICLE_PAGE_REVISIT,
  (isRevisit: boolean) => isRevisit
);

export const setLoadingAppointment = createAction(
  actionTypes.SET_LOADING_APPOINTMENT,
  (isLoading: boolean) => isLoading
);

export const setModifyEnabled = createAction(
  actionTypes.SET_MODIFY_ENABLED,
  (modifyEnabled: boolean) => modifyEnabled
);

export const setApptModified = createAction(
  actionTypes.SET_APPT_MODIFIED,
  (apptModified: boolean) => apptModified
);

export const setPopulateMobilePhoneNumber = createAction(
  actionTypes.SET_POPULATE_MOBILE_PHONE_NUMBER,
  (populateMobilePhoneNumber: boolean) => populateMobilePhoneNumber
);

export const setMobilePhoneNumberModified = createAction(
  actionTypes.SET_MOBILE_NUMBER_MODIFIED
);

export const setIdkSelected = createAction(
  actionTypes.SET_IDK_SELECTED,
  (apptModified: boolean) => apptModified
);

export const setStartEventSent = createAction(
  actionTypes.SET_START_EVENT_SENT,
  (startEventSent: boolean) => startEventSent
);

export const setTrimsSelectedPosition = createAction(
  actionTypes.SET_TRIMS_SELECTED_POSITION,
  (trimsSelectedPosition: number) => trimsSelectedPosition
);

export const setNoLongerAvailableDate = createAction(
  actionTypes.SET_NO_LONGER_AVAILABLE_DATE,
  (noLongerAvailableDate: boolean) => noLongerAvailableDate
);

export const setLastTimeslotSelected = createAction(
  actionTypes.SET_LAST_TIMESLOT_SELECTED,
  (lastTimeslotSelected: any) => lastTimeslotSelected
);
export const setSelectedTimeslotInPromoRange = createAction(
  actionTypes.SELECTED_TIMESLOT_IN_PROMO_RANGE,
  (selectedTimeslotInPromoRange: boolean) => selectedTimeslotInPromoRange
);

export const setContactInfoMissing = createAction(
  actionTypes.SET_CONTACT_INFO_MISSING,
  (contactInfoMissing: boolean) => contactInfoMissing
);

export const setSearchCriteriaFindMe = createAction(
  actionTypes.SET_SEARCH_CRITERIA_FIND_ME,
  (searchCriteriaFindMe: string) => searchCriteriaFindMe
);

export const setSelfCheckinEnabled = createAction(
  actionTypes.SET_SELF_CHECK_IN_ENABLED,
  (selfCheckinEnabled: boolean) => selfCheckinEnabled
);

export const setContactInfoFormValid = createAction(
  actionTypes.SET_CONTACT_INFO_FORM_VALID,
  (contactInfoFormValid: boolean) => contactInfoFormValid
);

export const setModelLabelOther = createAction(
  actionTypes.SET_MODEL_LABEL_OTHER,
  (modelLabelOther: string) => modelLabelOther
);

export const setMakeLabelOther = createAction(
  actionTypes.SET_MAKE_LABEL_OTHER,
  (makeLabelOther: string) => makeLabelOther
);

export const setQuickApptEnabled = createAction(
  actionTypes.SET_QUICK_APPT_ENABLED,
  (isQuickApptEnabled: string) => isQuickApptEnabled
);

export const setLocalizedQuickApptService = createAction(
  actionTypes.SET_LOCALIZED_QUICK_APPT_SERVICE,
  (localizedQuickApptService: string) => {
    return localizedQuickApptService;
  }
);

export const modifyEnabled = (): Thunk<void> => dispatch => {
  dispatch(setModifyEnabled(true));
};

export const apptModified = (): Thunk<void> => dispatch => {
  dispatch(setApptModified(true));
};

export const apptNotModified = (): Thunk<void> => dispatch => {
  dispatch(setApptModified(false));
};

export const mobilePhoneNumberModified = (): Thunk<void> => dispatch => {
  dispatch(setMobilePhoneNumberModified());
};

export const mobilePhoneNumberPopulated = (): Thunk<void> => dispatch => {
  dispatch(setPopulateMobilePhoneNumber(true));
};

export const mobilePhoneNumberNotPopulated = (): Thunk<void> => dispatch => {
  dispatch(setPopulateMobilePhoneNumber(false));
};

export const isSelfCheckinEnabled = (): Thunk<void> => dispatch => {
  dispatch(setSelfCheckinEnabled(false));
};

export const checkQuickApptEnabled = (): Thunk<Promise<any>> => async (
  dispatch,
  getState
) => {
  const isQuickApptEnabled =
    getQuickAppointment(getState()) &&
    getConsumerAllowAnyGreeterSel(getState()) &&
    getTellUsMoreSvcVisible(getState()) &&
    !!getOther(getState());

  await dispatch(setQuickApptEnabled(isQuickApptEnabled));
};

export const setGUID = createAction(actionTypes.SET_GUID, (guid: string) =>
  uuidv1()
);

export const setSavedMobile = createAction(
  actionTypes.SET_SAVED_MOBILE,
  (savedMobile: string) => savedMobile
);

export const setRecaptchaToken = createAction(
  actionTypes.SET_RECAPTCHA_TOKEN,
  (repactchaToken: string) => repactchaToken
);

export const dateApptPickerTouchByUser = createAction(
  actionTypes.DATE_APPT_PICKER_TOUCH_BY_USER,
  (dateApptPickerTouchByUser: boolean) => dateApptPickerTouchByUser
);

const actions = {
  setPrivateRoute,
  setApplicationFilterDaysBy,
  setAppFlow,
  setGtmFirstAvailableTimeslot,
  setNoFindsAlertShown,
  setLoadingAppointment,
  setModifyEnabled,
  setApptModified,
  modifyEnabled,
  apptModified,
  apptNotModified,
  setStartEventSent,
  mobilePhoneNumberPopulated,
  mobilePhoneNumberNotPopulated,
  isSelfCheckinEnabled,
  setContactInfoFormValid,
  setGUID,
  setSavedMobile
};

export type Action = ActionType<$Values<typeof actions>>;



// WEBPACK FOOTER //
// ./src/store/reducers/userInteractions/actions.js